import { formatDay } from '../game/daily';
import { mwLabel } from '../game/scoring';
import type { Progress } from '../game/types';
import { must } from './dom';
import { str } from './strings';
import { rankName } from './tiers';

export interface ArchiveRow {
  day: number;
  date: string;
  /** `null` when the grid hasn't been completed. */
  score: { mw: number; tier: string } | null;
  /** The reference was studied before this grid was perfected (GDD §4.3). */
  studied: boolean;
  /** This is the grid currently on the board. */
  current: boolean;
}

/** Newest grid first, all the way back to #1. */
export function buildArchiveRows(
  progress: Progress,
  today: number,
  currentDay: number | null,
): ArchiveRow[] {
  const rows: ArchiveRow[] = [];
  for (let d = today; d >= 1; d--) {
    const r = progress.results[d];
    rows.push({
      day: d,
      date: formatDay(d),
      score: r ? { mw: r.mw, tier: r.tier ?? '' } : null,
      studied: Boolean(progress.runs[d]?.studied),
      current: d === currentDay,
    });
  }
  return rows;
}

/** Render the archive list and hand back each row's day on click. */
export function renderArchive(rows: ArchiveRow[], onPick: (day: number) => void): void {
  const list = must('archList');
  list.innerHTML = rows
    .map((row) => {
      const rank = row.score ? rankName(row.score.tier) : '';
      // The studied mark is a word, not an emoji: no emoji renders inside
      // this interface, and 📖 belongs to the share (STYLEGUIDE §6). It sits
      // stacked above the score line, so the MW+tier reads on a single line
      // regardless of tier length (STYLEGUIDE §7).
      const studied = row.studied ? `<span class="studied">${str().rowStudied}</span>` : '';
      const right = row.score
        ? `<span class="score">${studied}<span class="mw-tier"><b>${mwLabel(row.score.mw)}</b> ${str().unitMW}${rank ? ' · ' + rank : ''}</span></span>`
        : `<span class="unplayed">${str().rowPlay}</span>`;
      return (
        `<button class="arch-row${row.current ? ' current' : ''}" data-day="${row.day}"` +
        `${row.current ? ' aria-current="true"' : ''}>` +
        `<span class="when"><span class="num">#${row.day}</span> · ${row.date}</span>${right}</button>`
      );
    })
    .join('');

  list.querySelectorAll<HTMLButtonElement>('.arch-row').forEach((b) => {
    b.addEventListener('click', () => onPick(Number(b.dataset.day)));
  });
}
